import React, { useCallback, useRef } from "react";
import DataGrid, {
  Column,
  Editing,
  MasterDetail,
  Paging,
} from "devextreme-react/data-grid";

import { useDispatch, useSelector } from "react-redux";
import {
  postResource,
  updateResource,
  deleteResource,
} from "../../redux/slices/resourceSlice.js";
import { handleNotificationMessage } from "../../utils/notifications.js";
import { DateBox } from "devextreme-react";
import { fromCustomDate, toCustomDate } from "../../utils/date.js";
import { getUserId } from "../../redux/slices/userSlice.js";
import CustomSelection from "../generic/CustomSelection.jsx";
import DateDisplay from "../generic/DateDisplay.jsx";
import VesselSystemGrid from "./VesselSystemGrid.jsx";
import ClientCell from "../generic/ClientCell.jsx";

const columns = [
  { dataField: "id", allowEditing: false, caption: "Id" },

  {
    dataField: "name",
    caption: "Name",
  },
  {
    dataField: "client_id",
    caption: "Client",
    cellRender: (data) => <ClientCell data={data} />,
  },
];

const resourceName = "vessel";
const VesselsDataGrid = ({ dataSource, clientId }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => getUserId(state));

  const gridRef = useRef(null);

  const onValueChanged = useCallback((rowIndex, value, dataField) => {
    gridRef.current.instance.cellValue(rowIndex, dataField, value);
  }, []);

  const onInitNewRow = useCallback(
    ({ data }) => {
      data.user_id = userId;
    },
    [userId, clientId]
  );

  const onSaving = useCallback(
    (e) => {
      e.changes.forEach((change) => {
        switch (change.type) {
          case "insert":
            dispatch(
              postResource({
                path: `${resourceName}`,
                data: change.data,
              })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              const { path, error } = payload;
              const data = payload.data;
              if (!data) return;
              const { id } = data;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          case "update":
            dispatch(
              updateResource({
                path: resourceName,
                id: change.key.id,
                data: change.data,
              })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              const { path, id, error } = payload;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          case "remove":
            dispatch(
              deleteResource({ path: resourceName, id: change.key.id })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              const { path, id, error } = payload;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          default:
            return;
        }
      });

      e.cancel = true;
    },
    [dispatch]
  );

  return (
    <React.Fragment>
      <DataGrid
        id="gridContainer"
        rowAlternationEnabled={true}
        dataSource={dataSource}
        allowColumnReordering={true}
        showBorders={true}
        onSaving={onSaving}
        onInitNewRow={onInitNewRow}
        ref={gridRef}
      >
        <Paging enabled={true} />
        <Editing
          mode="batch"
          allowUpdating={true}
          allowAdding={true}
          allowDeleting={true}
          allowSorting={false}
        />
        {columns.map((column, idx) => {
          if (column.type === "customSelection")
            return (
              <Column
                key={column.dataField}
                dataField={column.dataField}
                caption={column.caption}
                width={150}
                editCellRender={(cell) => {
                  return (
                    <CustomSelection
                      value={cell.data[column.dataField]}
                      dataSource={column.dataSource}
                      handleChange={(e) =>
                        onValueChanged(cell.rowIndex, e.value, column.dataField)
                      }
                    />
                  );
                }}
              />
            );
          else if (column.type === "date")
            return (
              <Column
                key={column.dataField}
                dataField={column.dataField}
                caption={column.caption}
                width={150}
                cellRender={DateDisplay}
                editCellRender={(cell) => (
                  <DateBox
                    displayFormat="dd/MM/yyyy"
                    onValueChanged={(e) =>
                      onValueChanged(
                        cell.rowIndex,
                        toCustomDate(e.value),
                        column.dataField
                      )
                    }
                    value={fromCustomDate(cell.data[column.dataField])}
                  />
                )}
              />
            );
          else
            return (
              <Column
                key={column.dataField}
                cellRender={column.cellRender}
                dataField={column.dataField}
                allowEditing={column.allowEditing}
                caption={column.caption}
                width={column.width}
              />
            );
        })}
        <MasterDetail
          enabled={true}
          component={({ data }) => <VesselSystemGrid data={data} />}
        />
      </DataGrid>
    </React.Fragment>
  );
};
export default VesselsDataGrid;
